export const CA_DOCUMENT_TYPE_VALUES = {
  // Parent
  FUEL_BILL: 'fuel_bill',
  ELECTRICITY_BILLS: '66e956203def64db6dd9b761',
  PROCUREMENT_BILLS: 'procurement_bills',
  SERVICE_BILLS: 'service_bills',
  BUSINESS_TRAVEL_TICKETS: 'business_travel_tickets',
  WATER_BILLS: 'water_bills',

  // Fuel Bill Children
  FOR_MACHINES: 'for_machines',
  FOR_VEHICLE: 'for_vehicle',
  FOR_REFRIGERANT: 'for_refrigerant',

  // Electricity Children
  RENEWABLE: 'renewable',
  NON_RENEWABLE: 'non_renewable',

  // Procurement
  FOR_RAW_MATERIAL: 'for_raw_material',
  RAW_MATERIAL_AVERAGE: 'row_material_average',
  RAW_MATERIAL_SUPPLIER_SPECIFIC: 'row_material_supplier_specific',

  FOR_CAPITAL_GOODS: 'for_capital_goods',
  CAPITAL_GOOD_AVERAGE: 'capital_good_average',
  CAPITAL_GOOD_SUPPLIER_SPECIFIC: 'capital_good_supplier_specific',

  // Service
  UPSTREAM_TRANSPORT: 'upstream_transport',
  DOWNSTREAM_TRANSPORT: 'downstream_transport'
} as const;

export const CA_DOCUMENT_TYPES_WITH_DOCUMENT_URL = [
  CA_DOCUMENT_TYPE_VALUES.FOR_MACHINES,
  CA_DOCUMENT_TYPE_VALUES.FOR_VEHICLE,
  CA_DOCUMENT_TYPE_VALUES.NON_RENEWABLE,
  CA_DOCUMENT_TYPE_VALUES.RENEWABLE,
  CA_DOCUMENT_TYPE_VALUES.BUSINESS_TRAVEL_TICKETS
] as const;

export type CADocumentTypeValue =
  (typeof CA_DOCUMENT_TYPE_VALUES)[keyof typeof CA_DOCUMENT_TYPE_VALUES];

/* eslint-disable @typescript-eslint/no-loss-of-precision */
export const CADocumentTypeOptions = [
  {
    label: 'Fuel Bill',
    value: CA_DOCUMENT_TYPE_VALUES.FUEL_BILL,
    hidden: false,
    children: [
      {
        label: 'For Machines',
        value: CA_DOCUMENT_TYPE_VALUES.FOR_MACHINES,
        hidden: false
      },
      {
        label: 'For Vehicle',
        value: CA_DOCUMENT_TYPE_VALUES.FOR_VEHICLE,
        hidden: false
      },
      {
        label: 'For Refrigerant',
        value: CA_DOCUMENT_TYPE_VALUES.FOR_REFRIGERANT,
        hidden: true
      }
    ]
  },
  {
    label: 'Electricity Bills',
    value: CA_DOCUMENT_TYPE_VALUES.ELECTRICITY_BILLS,
    hidden: false,
    children: [
      {
        label: 'Renewable',
        value: CA_DOCUMENT_TYPE_VALUES.RENEWABLE,
        hidden: true
      },
      {
        label: 'Non Renewable',
        value: CA_DOCUMENT_TYPE_VALUES.NON_RENEWABLE,
        hidden: false
      }
    ]
  },
  {
    label: 'Procurement Bills',
    value: CA_DOCUMENT_TYPE_VALUES.PROCUREMENT_BILLS,
    hidden: true,
    children: [
      {
        label: 'For Raw Material',
        value: CA_DOCUMENT_TYPE_VALUES.FOR_RAW_MATERIAL,
        hidden: false,
        children: [
          {
            label: 'Average',
            value: CA_DOCUMENT_TYPE_VALUES.RAW_MATERIAL_AVERAGE,
            hidden: false
          },
          {
            label: 'Supplier Specific',
            value: CA_DOCUMENT_TYPE_VALUES.RAW_MATERIAL_SUPPLIER_SPECIFIC,
            hidden: false
          }
        ]
      },
      {
        label: 'For Capital Goods',
        value: CA_DOCUMENT_TYPE_VALUES.FOR_CAPITAL_GOODS,
        hidden: false,
        children: [
          {
            label: 'Average',
            value: CA_DOCUMENT_TYPE_VALUES.CAPITAL_GOOD_AVERAGE,
            hidden: false
          },
          {
            label: 'Supplier Specific',
            value: CA_DOCUMENT_TYPE_VALUES.CAPITAL_GOOD_SUPPLIER_SPECIFIC,
            hidden: false
          }
        ]
      }
    ]
  },
  {
    label: 'Service Bills',
    value: CA_DOCUMENT_TYPE_VALUES.SERVICE_BILLS,
    hidden: true,
    children: [
      {
        label: 'Upstream Transport',
        value: CA_DOCUMENT_TYPE_VALUES.UPSTREAM_TRANSPORT,
        hidden: false
      },
      {
        label: 'Downstream Transport',
        value: CA_DOCUMENT_TYPE_VALUES.DOWNSTREAM_TRANSPORT,
        hidden: false
      }
    ]
  },
  {
    label: 'Business Travel Tickets',
    value: CA_DOCUMENT_TYPE_VALUES.BUSINESS_TRAVEL_TICKETS,
    hidden: false
  },
  {
    label: 'Water Bills',
    value: CA_DOCUMENT_TYPE_VALUES.WATER_BILLS,
    hidden: true
  }
];

export const PollingDBStatus = {
  ready_for_review: 'ready_for_review',
  uploaded: 'uploaded',
  failed: 'failed',
  in_progress: 'in_progress',
  submitted: 'submitted'
};

export const PollingStatusMessages = {
  processingFailed: 'Processing Failed, Please Re-Upload file and try again!'
};

export const getPollingErrorMessage = (response?: {
  data?: {error_message?: string};
  error_message?: string;
}): string =>
  response?.data?.error_message ??
  response?.error_message ??
  PollingStatusMessages.processingFailed;

export const StepState = {
  NORMAL: 'NORMAL',
  UPLOAD_AND_VALIDATE: 'UPLOAD_AND_VALIDATE',
  WITH_FILE_PREVIEW: 'WITH_FILE_PREVIEW'
};

export interface CADocumentTypeSelection {
  doc_type?: string;
  bill_type?: string;
  method?: string;
}

interface CADocumentTypeOptionNode {
  label: string;
  value: string;
  hidden?: boolean;
  children?: CADocumentTypeOptionNode[];
}

export const resolveCADocumentTypeFromFilter = (
  filterValue: string
): CADocumentTypeSelection => {
  if (!filterValue || filterValue === 'all') {
    return {};
  }

  const parent = CADocumentTypeOptions.find(
    (item) => item.value === filterValue
  );
  if (parent) {
    return {doc_type: filterValue};
  }

  for (const item of CADocumentTypeOptions) {
    if (!item.children) {
      continue;
    }

    const child = item.children.find((c) => c.value === filterValue);
    if (child) {
      return {doc_type: item.value, bill_type: filterValue};
    }

    for (const childItem of item.children as CADocumentTypeOptionNode[]) {
      if (!childItem.children) {
        continue;
      }

      const grandchild = childItem.children.find(
        (g) => g.value === filterValue
      );
      if (grandchild) {
        return {
          doc_type: item.value,
          bill_type: childItem.value,
          method: filterValue
        };
      }
    }
  }

  return {};
};

export const getVisibleCADocumentTypeSelection = (
  filterValue: string
): CADocumentTypeSelection => {
  const resolved = resolveCADocumentTypeFromFilter(filterValue);
  if (!resolved.doc_type) {
    return {};
  }

  const visibleParents = CADocumentTypeOptions.filter((item) => !item.hidden);
  const parent = visibleParents.find(
    (item) => item.value === resolved.doc_type
  );
  if (!parent) {
    return {};
  }

  const result: CADocumentTypeSelection = {doc_type: resolved.doc_type};

  if (!resolved.bill_type) {
    return result;
  }

  const visibleChildren =
    (parent.children as CADocumentTypeOptionNode[] | undefined)?.filter(
      (child) => !child.hidden
    ) ?? [];
  const child = visibleChildren.find((c) => c.value === resolved.bill_type);
  if (!child) {
    return result;
  }

  result.bill_type = resolved.bill_type;

  if (!resolved.method) {
    return result;
  }

  const grandchild = child.children?.find((g) => g.value === resolved.method);
  if (grandchild) {
    result.method = resolved.method;
  }

  return result;
};

export const getVisibleSubBillTypeOptions = (
  docType: string
): CADocumentTypeOptionNode[] => {
  const parent = CADocumentTypeOptions.find(
    (item) => item.value === docType && item.children
  );

  return (
    (parent?.children as CADocumentTypeOptionNode[] | undefined)?.filter(
      (child) => !child.hidden
    ) ?? []
  );
};

export const getVisibleMethodOptions = (
  docType: string,
  billType: string
): CADocumentTypeOptionNode[] => {
  const parent = CADocumentTypeOptions.find(
    (item) => item.value === docType && item.children
  );
  const billTypeNode = (
    parent?.children as CADocumentTypeOptionNode[] | undefined
  )?.find((child) => child.value === billType);

  return billTypeNode?.children ?? [];
};

export const applyCADocumentTypeAutoDefaults = (
  selection: CADocumentTypeSelection
): CADocumentTypeSelection => {
  const result: CADocumentTypeSelection = {...selection};

  if (!result.doc_type) {
    return result;
  }

  if (!result.bill_type) {
    const subBillTypeOptions = getVisibleSubBillTypeOptions(result.doc_type);
    if (subBillTypeOptions.length === 1) {
      result.bill_type = subBillTypeOptions[0].value;
    }
  }

  if (result.bill_type && !result.method) {
    const methodOptions = getVisibleMethodOptions(
      result.doc_type,
      result.bill_type
    );
    if (methodOptions.length === 1) {
      result.method = methodOptions[0].value;
    }
  }

  return result;
};
